import React, { useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { odata } from '@bingads-webui/http-util';
import legacyOdata from '@bingads-webui-component-legacy/http/channel/odata';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { useEditImageService } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { getAssetEnhancementSettings } from '@bingads-webui-campaign/image-experience-utils';

import { getImageBulkEditODataService, getFolderODataService, getBackgroundChangeODataService } from './odata-service';
import { getEditImageRepository } from './repository';
import { prepareODataConfig } from './with-ads-studio-services-provider';
import { getLoggerWithoutPageInfo, getLogVersion } from './logger/custom-logger';

export const withEditImageService = (WrappedComponent, propName = 'dataService') => {
  const Wrapper = (props) => {
    const {
      aid, cid, accountTimeZone, oDataConfig, i18n, dateFormat, permissions, activity,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    const versionForLog = useMemo(() => (getLogVersion(permissions)), [permissions]);
    const logger = useMemo(() => getLoggerWithoutPageInfo(activity, versionForLog), [activity, versionForLog]);

    const imageODataService = useMemo(
      () => new ImageOData(cid, aid, oDataConfigObj),
      [oDataConfigObj, aid, cid]
    );

    const imageBulkEditODataService = useMemo(
      () => getImageBulkEditODataService({
        odata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
      }),
      [oDataConfigObj, aid, cid]
    );

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [oDataConfigObj, aid, cid, i18n, dateFormat]
    );

    const backgroundChangeODataService = useMemo(
      () => getBackgroundChangeODataService({
        odata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
      }),
      [oDataConfigObj, aid, cid]
    );

    const repository = useMemo(() => getEditImageRepository({
      imageODataService,
      imageBulkEditODataService,
      backgroundChangeODataService,
      folderODataService,
      i18n,
      ianaTimeZone,
      timezoneUTCOffset,
    }), [imageODataService, imageBulkEditODataService, backgroundChangeODataService, folderODataService, i18n, ianaTimeZone, timezoneUTCOffset]);

    const {
      getImagesWithFilter,
      getImagesWithBlur,
      getImagesWithFilterAndBlur,
      imagesWithFilter,
      imagesWithFilterAndBlur,
      removeImageBackground,
      generateImageBackground,
      generateMultipleImageBackgrounds,
      getImageSegmentations,
      saveImage,
      saveImageCopy,
      cropImage,
      getImageById,
      uploadImageToFolder,
      saveImageWithBaseId,
      uploadStockImage,
      moveImageToFolder,
      imageFit,
      addImageWithBaseIdToFolder,
    } = useEditImageService({
      repository,
      logger,
    });

    const dataService = useMemo(() => ({
      getImagesWithFilter,
      getImagesWithBlur,
      getImagesWithFilterAndBlur,
      imagesWithFilter,
      imagesWithFilterAndBlur,
      removeImageBackground,
      generateImageBackground,
      generateMultipleImageBackgrounds,
      getImageSegmentations,
      saveImage,
      saveImageCopy,
      cropImage,
      getImageById,
      uploadStockImage,
      uploadImageToFolder,
      saveImageWithBaseId,
      moveImageToFolder,
      imageFit,
      addImageWithBaseIdToFolder,
    }), [getImagesWithFilter, getImagesWithBlur, getImagesWithFilterAndBlur, imagesWithFilter, imagesWithFilterAndBlur, removeImageBackground, generateImageBackground, generateMultipleImageBackgrounds, getImageSegmentations, saveImage, saveImageCopy, cropImage, getImageById, uploadStockImage, uploadImageToFolder, saveImageWithBaseId, moveImageToFolder, imageFit, addImageWithBaseIdToFolder]);

    const getAssetEnhancementSettingsService = useMemo(() => () => getAssetEnhancementSettings({
      aid,
      cid,
      oDataConfig: oDataConfigObj,
    }), [aid, cid, oDataConfigObj]);

    return (
      <WrappedComponent
        {...props}
        {...{ [propName]: dataService }}
        {...{ getAssetEnhancementSettings: getAssetEnhancementSettingsService }}
      />
    );
  };

  Wrapper.displayName = `withEditImageService(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
};
